import styled from 'styled-components';

import { Colors, Theme } from '../types';

import { IUnitBox } from './index';
import { Text } from '../Text';
import { HTMLAttributes } from 'react';
import { iconType } from '../NewInput/types';

const getRightPadding = (data: {
    rightIcon: boolean | undefined;
    isFocus: boolean | undefined;
    unit: string | undefined;
}) => {
    const { rightIcon, isFocus, unit } = data;

    const defaultPadding = unit ? '37px' : '15px';
    const focusedPadding = unit ? '70px' : '51px';

    return isFocus || rightIcon ? focusedPadding : defaultPadding;
};

const getAutofillColors = ({
    errorStatus,
    successStatus,
}: Record<string, boolean | undefined>): { bgc: Colors; borderColor: Colors } => {
    switch (true) {
        case errorStatus:
            return { bgc: 'surface-hovered', borderColor: 'critical-highlight' };

        case successStatus:
            return { bgc: 'surface-default', borderColor: 'success-highlight' };

        default:
            return { bgc: 'surface-default', borderColor: 'border-default' };
    }
};

export const InputWrapper = styled.div<{ wrapperMarginBottom?: string }>`
    position: relative;
    margin-bottom: ${({ wrapperMarginBottom }) => wrapperMarginBottom ?? '40px'};
    padding-top: 20px;
`;

export const HelperText = styled(Text)`
    position: absolute;
    bottom: 0;
    padding-left: 12px;
    transform: translateY(calc(100% + 4px));
`;

export const InputBox = styled.div`
    position: relative;
`;

export const InputLabel = styled.label<{$touched?: boolean; theme: Theme}>`
    display: inline-block;
    font-family: 'Roboto', sans-serif;
    padding-left: 12px;
    transform: translateY(calc(-100% - 4px));
    color: ${({ theme }) => theme['text-secondary']};
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    position: absolute;
`;

export const LeftBox = styled.div`
    position: absolute;
    top: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    left: 12px;
    width: 20px;
    height: 20px;
`;
export const RightBox = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 50%;
    transform: translateY(-50%);
    right: 12px;
    width: 20px;
    height: 20px;
`;

export const ImgIcon = styled.div`
    width: 100%;
    height: auto;
    object-fit: contain;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const Img = styled.img`
    width: 20px;
    height: 20px;
    object-fit: contain;
`;

export const TogglePasswordVisibility = styled.button`
    background: none;
    cursor: pointer;
`;

export const ClearButton = styled.button<{ $isInputFocus?: boolean }>`
    outline: none;
    cursor: pointer;
    background-color: transparent;
    display: ${({ $isInputFocus }) => ($isInputFocus ? 'flex' : 'none')};
    transition: 0.1s;
    width: 20px;
    opacity: ${({ $isInputFocus }) => ($isInputFocus ? 1 : 0)};
    visibility: ${({ $isInputFocus }) => ($isInputFocus ? 'visible' : 'hidden')};
`;

export const UnitBox = styled.div<IUnitBox>`
    display: flex;
    align-items: center;
`;

export const StyledInput = styled.input<{$isFocus?: boolean, $rightIcon?: iconType, $leftIcon?: iconType, $unit?: string; $errorStatus?: boolean; $successStatus?: boolean, $touched: boolean} & HTMLAttributes<HTMLInputElement>>`
    width: 100%;
    font-size: 16px;
    line-height: 22px;
    border-width: 1px;
    border-style: solid;
    border-radius: ${({ theme }) => `${theme['border-radius-medium']}px`};
    background-color: ${({ theme }) => theme['surface-default']};
    border-color: ${({ theme }) => theme['border-default']};
    color: ${({ theme }) => theme['text-main']};
    padding-top: 15px;
    padding-bottom: 15px;
    padding-right: ${({ $rightIcon, $isFocus, $unit }) =>
        getRightPadding({ rightIcon: Boolean($rightIcon), isFocus: $isFocus, unit: $unit })};
    padding-left: ${({ $leftIcon }) => ($leftIcon ? '43px' : '15px')};

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        margin: 0;
        -webkit-appearance: none;
    }

    &[type='number'] {
        -moz-appearance: textfield;
    }

    &::placeholder {
        font-size: 16px;
        line-height: 24px;
        color: ${({ theme }) => theme['text-secondary-subdued']};
        transition: opacity 0.1s linear;
    }

    &:focus::placeholder {
        opacity: 0;
    }

    &:focus {
        background-color: ${({ theme }) => theme['surface-hovered']};
        border-color: ${({ theme }) => theme['safe']};
    }

    &:disabled {
        background-color: ${({ theme }) => theme['surface-secondary']};
        border-color: transparent;
    }

    &:disabled::placeholder,
    &:disabled {
        color: ${({ theme }) => theme['text-secondary-subdued']};
    }

    ${({ theme, $errorStatus, $touched }) =>
        $errorStatus &&
        $touched &&
        // TODO: change colors to correct
        `background-color: ${theme['surface-hovered']};
            border-color: ${theme['critical-highlight']};`};

    ${({ theme, $successStatus }) => $successStatus && `border-color: ${theme['success-highlight']};`};

    // fix for autocomplete
    &:-webkit-autofill {
        border-color: ${({ theme, $errorStatus, $successStatus }) =>
            theme[getAutofillColors({ errorStatus: $errorStatus, successStatus: $successStatus }).borderColor]} !important;
        box-shadow: inset 0 0 100px
            ${({ theme, $errorStatus, $successStatus }) => theme[getAutofillColors({ errorStatus: $errorStatus, successStatus: $successStatus }).bgc]} !important;
    }
`;
